import { Capacitor, CapacitorHttp, type HttpResponse } from '@capacitor/core';
import { dbg } from '../debug/debugLog.js';

/** A WebView may report online while its fetch transport cannot reach Edge.
 * Only replay read endpoints; writes keep their existing durable outbox retry. */
export async function fetchSyncReadTransport(
  url: string,
  init: RequestInit,
  assertScope: () => void,
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (error) {
    const name = new URL(url).pathname.split('/').at(-1);
    if (!(error instanceof TypeError) || init.signal?.aborted
      || !Capacitor.isNativePlatform() || !['sync-pull', 'sync-head'].includes(name ?? '')) throw error;
    assertScope();
    dbg.warn('mobile-sync', 'webview_read_native_fallback', { endpoint: name });
    const signal = init.signal;
    if (signal?.aborted) throw signal.reason;
    let abort!: () => void;
    const aborted = new Promise<never>((_, reject) => {
      abort = () => reject(signal?.reason ?? new DOMException('Request aborted', 'AbortError'));
      signal?.addEventListener('abort', abort, { once: true });
    });
    let response: HttpResponse;
    try {
      response = await Promise.race([
        CapacitorHttp.request({
          url, method: init.method,
          headers: Object.fromEntries(new Headers(init.headers).entries()),
          data: typeof init.body === 'string' ? JSON.parse(init.body) : undefined,
          responseType: 'json', connectTimeout: 10_000, readTimeout: 10_000,
          disableRedirects: true,
        }),
        aborted,
      ]);
    } catch (nativeError) {
      if (signal?.aborted) throw signal.reason ?? nativeError;
      dbg.warn('mobile-sync', 'native_read_failed', { endpoint: name });
      throw error;
    } finally {
      signal?.removeEventListener('abort', abort);
    }
    assertScope();
    dbg.info('mobile-sync', 'native_read_response', { endpoint: name, status: response.status });
    return new Response([204, 205, 304].includes(response.status) ? null
      : typeof response.data === 'string' ? response.data : JSON.stringify(response.data),
    { status: response.status });
  }
}
